import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import "./App.css";
import Home from "./landing-screen/home";
import LandingLayout from "./layouts/LandingLayout";
import DashboardLayout from "./layouts/DashboardLayout";
import DashboardHome from "./dashboard/Home";
import TermService from "./landing-screen/TermService";
import PrivacyPolicy from "./landing-screen/PrivacyPolicy";
import Register from "./landing-screen/Register";
import About from "./landing-screen/about";
import FAQPage from "./landing-screen/FAQ";
import HowItWorks from "./landing-screen/HowItWorks";
import ReferralPage from "./landing-screen/ReferralPage";
import DeleteAccount from "./landing-screen/DeleteAccount";
import SurveyPageCallback from "./landing-screen/surveypage";
import PersonalDetails from "./dashboard/PersonalDetails";
import Transaction from "./dashboard/Transaction";
import Referral from "./dashboard/Referral";
import SignOut from "./dashboard/SignOut";
import SurveyPage from "./dashboard/Survey";
import SurveyDetail from "./dashboard/SurveyDetail";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "./redux/slice/authSlice";
import { jwtDecode } from "jwt-decode";
import Earnings from "./dashboard/Earnings";
import Reward from "./dashboard/Reward";
import Prescreener from "./dashboard/Prescreener";

import FeedbackPage from "./dashboard/Feedback";
import FeedbackSurveyDetail from "./dashboard/FeedbackSurveyDetail";

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

// Checks the stored JWT is present and not expired. This only controls
// which route shell renders client-side — the real security boundary is the
// backend rejecting unauthenticated API calls — but it stops the UI from
// treating a stale/expired session as logged in.
const hasValidToken = () => {
  try {
    const rawToken = localStorage.getItem("token");
    if (!rawToken) return false;
    const token = JSON.parse(rawToken);
    if (!token) return false;
    const decoded = jwtDecode(token);
    return !decoded.exp || decoded.exp * 1000 > Date.now();
  } catch (error) {
    return false;
  }
};

// An expired access token is still a usable session while a refresh token
// exists — the axios interceptor (redux/slice/apiAuth.js) renews it on the
// first 401.
const hasRefreshToken = () => {
  try {
    return !!JSON.parse(localStorage.getItem("refreshToken"));
  } catch (error) {
    return false;
  }
};

function AppContent() {
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggedInres, setisLoggedInres] = useState(false);
  const authStatus = useSelector((state) => state.auth);
  const isLoggedIn = authStatus.isLoggedIn;
  const dispatch = useDispatch();

  useEffect(() => {
    const hasSession = hasValidToken() || hasRefreshToken();
    // Redux says logged in (a stored "user") but there is no usable token.
    // Clear the stale session so redux and the route shell agree; otherwise
    // the landing pages redirect to /dashboard, which isn't routed, which
    // redirects back to "/" — an endless navigate loop ("Maximum update
    // depth exceeded").
    if (isLoggedIn && !hasSession) {
      dispatch(logout());
    }
    setisLoggedInres(isLoggedIn && hasSession);
    setIsLoading(false);
  }, [isLoggedIn, dispatch]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Routes>
      {isLoggedInres ? (
        <>
          <Route element={<DashboardLayout />}>
            <Route path="/dashboard" element={<DashboardHome />} />
            <Route path="/feedback" element={<FeedbackPage />} />
            <Route path="/feedback-survey/:surveyId" element={<FeedbackSurveyDetail />} />
            <Route path="/personal-details" element={<PersonalDetails />} />
            <Route path="/transaction" element={<Transaction />} />
            <Route path="/refer" element={<Referral />} />
            <Route path="/survey" element={<SurveyPage />} />
             <Route path="/survey/:surveyId" element={<SurveyDetail />} />
            <Route path="/passbook" element={<Earnings />} />
            <Route path="/earning" element={<Earnings />} />
            <Route path="/reward" element={<Reward />} />
            <Route path="/pre-screener" element={<Prescreener />} />
            <Route path="*" element={<Navigate to="/dashboard" />} />
             <Route path="/survey-callback" element={<SurveyPageCallback />} />
          </Route>
          {/* Standalone — deliberately outside DashboardLayout. SideNav's
              Points component fetches the profile on mount, which raced
              with logout clearing the token (401 → axios interceptor
              silently refreshes a new token mid-logout) and could spiral
              into "Maximum update depth exceeded". Sign-out needs nothing
              from the dashboard chrome anyway. */}
          <Route path="/sign-out" element={<SignOut />} />
          {/* Same public page for a logged-in visitor, with the site header/footer */}
          <Route element={<LandingLayout />}>
            <Route path="/delete-account" element={<DeleteAccount />} />
          </Route>
        </>
      ) : (
        <Route element={<LandingLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/register" element={<Register />} />
          <Route path="/term-service" element={<TermService />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/about-us" element={<About />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/how-referral-works" element={<ReferralPage />} />
          {/* Public — no login needed (account-deletion URL for Google Play) */}
          <Route path="/delete-account" element={<DeleteAccount />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Route>
      )}
    </Routes>
  );
}

export default App;
